import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentCheckStore } from 'Src/feature/check/CurrentCheckProvider';
import { Check, CheckItem } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { DiscountReason, PromoCode, StandardDiscountWithSchedules } from 'Src/feature/discount/discountService';
import { useDiscount } from 'Src/feature/discount/useDiscount';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useCurrentOperator } from 'Src/feature/operator/useCurrentOperator';
import { useToast } from 'Src/feature/toasts/useToast';
import { sortByName } from 'Src/utils/sort';
import { definitions } from 'Src/vendor/posapi/pos';

const sectionId = 'useCheckItemDiscounts';

type AutoDiscountOptions = definitions['check.AutoDiscountOptions'];
type CheckItemDiscountUI = StandardDiscountWithSchedules & {
  is: {
    /**
     * @description Indicates the discount was applied to the check/item.
     * It needs to be used in conjunction with enabled flag.
     */
    applied: boolean,
    /**
     * @description Indicates if the discount is enabled in its application to the check/item.
     * It could be false if either of these things have happened:
     * - Discount removed from item (sets status to Removed)
     * - Discount is auto-applied and the server will automatically handle changing its status
     *   to Enabled/Disabled as the check/item progresses.
     */
    enabled: boolean,
  };
  onCheckItems: CheckItem['discounts'];
}

interface UseCheckItemDiscountsOptions {
  checkId?: Check['id'] | undefined;
  checkItemIds?: CheckItem['id'][];
  contextId?: string;
}

const useCheckItemDiscounts = (options: UseCheckItemDiscountsOptions) => {
  const { t } = useTranslation();
  const toast = useToast();

  const { checkId, checkItemIds } = options || {};

  const currentCheck = useCurrentCheckStore({ checkId });
  const currentOperator = useCurrentOperator();

  const discounts = useDiscount.query.getAll();
  const createDiscount = useCheck.mutate.createDiscount();
  const removeDiscount = useCheck.mutate.removeDiscount();

  const updateDiscount = useCheck.mutate.updateDiscount();
  const updateAutoDiscount = useCheck.mutate.updateAutoDiscount();

  // stored active values are necessary because
  // 1. ui
  // 2. save reference to active discount
  // 3. open modal that needs access to active value
  // 4. modal appends more values
  // 5. save discount updates after/from multiple ui layers
  const [active, activeSet] = useState({
    promoCode: undefined as undefined | PromoCode,
    reason: undefined as undefined | DiscountReason,
    discount: undefined as undefined | CheckItemDiscountUI,
    openValue: undefined as undefined | number,
    note: undefined as undefined | string,
    id: undefined as undefined | string,
  });

  const applyDiscountReason = (reasonId: number, openValue: number | undefined = undefined, note?: string) => {
    const reason = active.discount?.reasons.find(({ id }) => id === reasonId);
    if (!reason) {
      toast.showAndAutoHide.warning({ title: t('discount.labels.checkItemDiscountTitle'), message: t('discount.validation.reasonNotFound', { reasonId }) });
      return { toastShown: true };
    }
    activeSet({
      ...active,
      reason,
      openValue,
      note,
    });
    sendStoreValues({ reason, openValue, note, discount: active.discount, promoCode: active.promoCode });
    return { toastShown: false };
  };

  const checkItems = (currentCheck.query.data?.items ?? []).filter(({ id }) => (checkItemIds ?? []).includes(id));

  const manageDiscountsEntitlement = currentOperator.permission('pos.checks.discounts.manage');
  // TODO - pos.checks.discounts.create and pos.checks.discounts.delete will be depracated in the future, per product
  const createDiscountsEntitlement = currentOperator.permission('pos.checks.discounts.create');

  const operatorCanManageManual = (discount: StandardDiscountWithSchedules) => {
    // need to account for old entitlement (pos.checks.discounts.create) until merchants are all converted
    if (createDiscountsEntitlement.isAllowed) return true;
    // permission must be granted
    if (!manageDiscountsEntitlement.isAllowed) return false;
    // discount must exist on metadata
    if (manageDiscountsEntitlement.metadata?.allowAll || (manageDiscountsEntitlement.metadata?.standardDiscountIds ?? []).includes(discount.id)) return true;
    // discount does not exist on metadata
    return false;
  };

  const operatorCanManageAuto = (discount: CheckItem['discounts'][number]) => {
    // need to account for old entitlement (pos.checks.discounts.create) until merchants are all converted
    if (createDiscountsEntitlement.isAllowed) return true;
    // permission must be granted
    if (!manageDiscountsEntitlement.isAllowed) return false;
    // discount must exist on metadata
    if (
      manageDiscountsEntitlement.metadata?.allowAll ||
      (!!discount.standardDiscountId && (manageDiscountsEntitlement.metadata?.standardDiscountIds ?? []).includes(discount.standardDiscountId))
    ) {
      return true;
    }
    // discount does not exist on metadata
    return false;
  };

  const combined = {
    menuItem: { price: 0, ids: [] as number[] },
  };
  checkItems?.forEach((item) => {
    if (item?.menuItem) {
      if (item.menuItem.price) combined.menuItem.price += item.menuItem.price;
      if (item.menuItem.menuItemId) combined.menuItem.ids.push(item.menuItem.menuItemId);
    }
  });

  const multiCombined = checkItems.map((item) => ({ menuItemId: item.menuItem.menuItemId, price: item.menuItem.price }));

  const allowedFormMenuItem = (discount: StandardDiscountWithSchedules): boolean => {
    // If discount.menuItemIds is empty, then the discount is meant to apply to all menu items
    if (!discount.menuItemIds || !discount.menuItemIds.length) return true;
    // If discount.menuItemIds is not empty, only these menuItem(s) can be used with this discount
    return multiCombined.every(({ menuItemId }) => (discount.menuItemIds ?? []).includes(menuItemId));
  };

  const isApplicable = (discount: StandardDiscountWithSchedules) => {
    if (!discount.isEnabled) return false;
    // If discount.application is of type Check then check level discount can be applied to only specific items on a check
    // If discount.application is of type Item ( CheckItem ) then it is meant to only apply to a check item
    if (discount.application !== 'Item' && discount.application !== 'Both') return false;
    return allowedFormMenuItem(discount);
  };

  // get discount(s) form all check items in the store
  // or a specific check item
  const collectCheckItemDiscounts = (opts: { checkItemId?: CheckItem['id'], standardDiscountId: StandardDiscountWithSchedules['id'] }) => {
    const matches: CheckItem['discounts'] = [];
    checkItems?.forEach((checkItem) => {
      // break out if we want to only search a specific check item
      if (opts.checkItemId && checkItem?.id !== opts.checkItemId) return;
      (checkItem?.discounts || []).forEach((checkItemDiscount) => {
        // do not pull any that may have been removed previously
        if (checkItemDiscount.status !== 'Enabled') return;
        if (checkItemDiscount.standardDiscountId === opts.standardDiscountId) matches.push(checkItemDiscount);
      });
    });
    return matches;
  };

  // filtered subset of all discounts
  const checkItemDiscountUI = discounts.data
    // filtered subset of applicable and allowed discounts
    ?.filter((discount) => isApplicable(discount) && operatorCanManageManual(discount))
    // add some extra properties to the discount model for ui
    .map((discount) => {
      // default to 'currency'
      const checkItemDiscounts = collectCheckItemDiscounts({ standardDiscountId: discount.id });
      const is = {
        applied: !!checkItemDiscounts.length,
        enabled: !!checkItemDiscounts.find((d) => d.status === 'Enabled')
      };
      return Object.assign(discount, { is, onCheckItems: checkItemDiscounts });
    })
    // sort discounts alphabetically
    .sort(sortByName);

  // we only care about auto discounts when a single check item is selected
  const allAutoDiscounts = checkItems.length === 1 ? checkItems.flatMap(({ discounts }) => discounts).filter((d) => d.doAutoApply && operatorCanManageAuto(d)) : [];
  const autoCheckItemDiscountUI = [...new Map(allAutoDiscounts.map((d) => [d.standardDiscountId, d])).values()]
    .map((d) => {
      const is = { applied: !d.isAutoDisabled, enabled: d.status === 'Enabled' };
      return Object.assign(d, { is });
    })
    .sort(sortByName);

  const has = {
    any: (checkItemDiscountUI && checkItemDiscountUI?.length > 0) || (autoCheckItemDiscountUI && autoCheckItemDiscountUI?.length > 0),
    someWithPromoCodes: checkItemDiscountUI && checkItemDiscountUI?.length > 0 && checkItemDiscountUI?.some((discount) => discount.promoCodes && discount.promoCodes.length)
  };

  const getPromoCodeFromDiscount = (code: string, discount: StandardDiscountWithSchedules) =>
    discount.promoCodes?.find((promoCode) => promoCode.code.toUpperCase() === code.toUpperCase());

  const getDiscountFromPromoCode = (code: string) =>
    checkItemDiscountUI?.find((discount) => getPromoCodeFromDiscount(code, discount) !== undefined);

  const modal = {
    openDiscount: {
      ...useModalControl(),
      onApply: (reasonId: number, openValue: number, note?: string) => {
        const outcome = applyDiscountReason(reasonId, openValue, note);
        if (!outcome.toastShown) {
          modal.openDiscount.close();
        }
      },
    }
  };

  const onDiscountAdd = (discount: CheckItemDiscountUI) => {
    activeSet({
      ...active,
      discount,
    });
    const { type, reasons } = discount;
    if (type === 'OpenAmount' || type === 'OpenPercent' || (reasons.length > 0 && active.reason === undefined)) {
      modal.openDiscount.open();
    } else {
      sendStoreValues({ discount, reason: active.reason, promoCode: active.promoCode });
    }
  };

  const onDiscountEdit = (discount: CheckItemDiscountUI) => {
    // Find the check item discount data (first one if multiple items)
    const checkItemDiscount = discount.onCheckItems[0];

    if (!checkItemDiscount) return;

    // Pre-load active state with existing discount data
    const reason = discount.reasons.find((r) => r.id === checkItemDiscount.reasonId);

    activeSet({
      discount,
      reason,
      openValue: checkItemDiscount.value,
      note: checkItemDiscount.note,
      id: checkItemDiscount.id,
      promoCode: active.promoCode,
    });

    modal.openDiscount.open();
  };


  const sendStoreValues = (args: { reason?: DiscountReason, openValue?: number, note?: string, discount?: CheckItemDiscountUI, promoCode?: PromoCode }) => {
    const { discount, reason, openValue, note, promoCode } = args;

    // this is mainly just for developer debugging, and as a typescript guard,
    // all other castings in the payload will get caught by the server
    if (!discount?.id) return toast.showAndAutoHide.warning({ title: t('discount.labels.checkItemDiscountTitle'), message: t('discount.validation.notSelected') });
    if (!currentCheck.query.data) return toast.showAndAutoHide.warning({ title: t('discount.labels.checkItemDiscountTitle'), message: t('discount.validation.checkMissing') });

    // confirm we passed in the correct items and some bad undefined data did not make it through
    const items = checkItems?.filter((item) => Boolean(item));
    if (!items || !items.length) return toast.showAndAutoHide.warning({ title: t('discount.labels.checkItemDiscountTitle'), message: t('discount.validation.checkItemsMissing') });

    // blank out any stored values ( even if the socket fails )
    activeSet({
      promoCode: undefined,
      openValue: undefined,
      reason: undefined,
      discount: undefined,
      note: undefined,
      id: undefined,
    });


    if (active.id) {
      updateDiscount.mutate({
        checkId: currentCheck.query.data.id,
        discountId: active.id,
        note: note || '',
      });
      return;
    }

    createDiscount.mutate({
      checkId: currentCheck.query.data.id,
      discount: {
        application: 'Item',
        standardDiscountId: discount.id,
        itemIds: items.map(({ id }) => id),
        type: discount.type === 'FixedPercent' || discount.type === 'OpenPercent'
          ? 'Percent'
          : 'Amount',
        value: (discount.type === 'FixedAmount' || discount.type === 'FixedPercent'
          ? discount.fixedValue
          : openValue) as number,
        reason: reason?.reason ?? '',
        reasonId: reason?.id,
        note,
        promoCodeId: promoCode?.id,
      },
    }, {
      onError: (e) => {
        toast.show.serviceError(e, { id: `${sectionId}-sendStoreValues-createDiscount` });
      },
    });
  };

  const onDiscountRemove = (discount: CheckItemDiscountUI) => {
    // search for applied discount model ids
    const appliedDiscountIds = discount.onCheckItems.filter(({ standardDiscountId }) => discount.id === standardDiscountId).map(({ id }) => id);
    if (!appliedDiscountIds.length) return toast.showAndAutoHide.warning({ title: t('discount.labels.checkItemDiscountTitle'), message: t('discount.validation.checkDiscountMissing') });
    removeDiscount.mutate({
      checkId: currentCheck.query.data?.id as number,
      discountIds: appliedDiscountIds,
      // the API will error out if discountId is not included in the payload, so just use the first one in the array
      // the endpoint still takes into account the array
      discountId: appliedDiscountIds[0],
    }, {
      onError: (e) => toast.show.serviceError(e, { id: `${sectionId}-onDiscountRemove-removeDiscount` }),
    });
  };

  const onUpdateAutoDiscount = (discount: AutoDiscountOptions) => {
    updateAutoDiscount.mutate({
      checkId: currentCheck.query.data?.id as number,
      discount,
    }, {
      onError: (e) => toast.show.serviceError(e, { id: `${sectionId}-onUpdateAutoDiscount-updateAutoDiscount` }),
    });
  };

  return {
    store: {
      // read only
      discounts: checkItemDiscountUI,
      autoDiscounts: autoCheckItemDiscountUI,
      check: currentCheck.query.data,
      checkItems,
      has,
      active,
      combined,
      checkItemDiscountUI,
      // actions
      modal,
      isApplicable,
      allowedFormMenuItem,
      getDiscountFromPromoCode,
      getPromoCodeFromDiscount,
      collectCheckItemDiscounts,
      onDiscountAdd,
      onDiscountEdit,
      sendStoreValues,
      onDiscountRemove,
      onUpdateAutoDiscount,
      updateAutoDiscount,
    }
  };
};


export { useCheckItemDiscounts };
export type { CheckItemDiscountUI };
